import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  SHELL_RUNTIME_TOAST_EVENT,
  type ShellUiToastInput,
  type ShellUiToastTone,
} from "@shared/host/shell-runtime";

const MAX_TOASTS = 4;
const DEFAULT_TOAST_DURATION_MS = 4000;
const ERROR_TOAST_DURATION_MS = 8000;

type ShellToastItem = Required<
  Pick<ShellUiToastInput, "description">
> &
  Omit<ShellUiToastInput, "description"> & {
    id: string;
    tone: ShellUiToastTone;
  };

const resolveToastTone = (
  tone?: ShellUiToastInput["tone"],
): ShellUiToastTone => tone ?? "info";

const resolveToastDuration = (
  input: ShellUiToastInput,
  tone: ShellUiToastTone,
) => input.durationMs ?? (tone === "error" ? ERROR_TOAST_DURATION_MS : DEFAULT_TOAST_DURATION_MS);

const resolveToastTitle = (
  input: ShellUiToastInput,
  tone: ShellUiToastTone,
  translate: (key: string) => string,
) => {
  if (input.title?.trim()) {
    return input.title;
  }

  if (tone === "error") {
    return translate("toasts.error");
  }

  if (tone === "success") {
    return translate("toasts.success");
  }

  if (tone === "warning") {
    return translate("toasts.warning");
  }

  return translate("toasts.info");
};

const createToastId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `shell-toast-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const toneClassNames: Record<ShellUiToastTone, string> = {
  info: "border-border-subtle bg-surface-primary text-content-primary",
  success: "border-state-success/30 bg-state-successSoft text-content-primary",
  warning: "border-state-warning/30 bg-state-warningSoft text-content-primary",
  error: "border-state-danger/30 bg-state-dangerSoft text-content-primary",
};

export const ShellToastViewport = () => {
  const { t } = useTranslation("layout");
  const [items, setItems] = useState<ShellToastItem[]>([]);
  const timersRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const dismissToast = (toastId: string) => {
      const timerId = timersRef.current[toastId];
      if (timerId) {
        window.clearTimeout(timerId);
        delete timersRef.current[toastId];
      }

      setItems((currentItems) => currentItems.filter((item) => item.id !== toastId));
    };

    const handleToastEvent = (event: Event) => {
      const customEvent = event as CustomEvent<ShellUiToastInput>;
      const tone = resolveToastTone(customEvent.detail.tone);
      const toastId = customEvent.detail.id ?? createToastId();
      const nextToast: ShellToastItem = {
        ...customEvent.detail,
        id: toastId,
        tone,
        title: resolveToastTitle(customEvent.detail, tone, t),
        description: customEvent.detail.description ?? "",
        durationMs: resolveToastDuration(customEvent.detail, tone),
      };

      setItems((currentItems) => {
        const dedupedItems = currentItems.filter((item) => item.id !== toastId);
        return [nextToast, ...dedupedItems].slice(0, MAX_TOASTS);
      });

      const previousTimer = timersRef.current[toastId];
      if (previousTimer) {
        window.clearTimeout(previousTimer);
      }

      timersRef.current[toastId] = window.setTimeout(() => {
        dismissToast(toastId);
      }, nextToast.durationMs);
    };

    window.addEventListener(
      SHELL_RUNTIME_TOAST_EVENT,
      handleToastEvent as EventListener,
    );

    return () => {
      window.removeEventListener(
        SHELL_RUNTIME_TOAST_EVENT,
        handleToastEvent as EventListener,
      );

      Object.values(timersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      timersRef.current = {};
    };
  }, [t]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-4 z-[120] flex justify-center px-4"
    >
      <div className="flex w-full max-w-sm flex-col gap-3">
        {items.map((item) => (
          <div
            key={item.id}
            className={`pointer-events-auto rounded-xl border px-4 py-3 text-sm shadow-sm ${toneClassNames[item.tone]}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="font-medium">{item.title}</p>
                {item.description ? (
                  <p className="mt-1 text-xs text-content-secondary">{item.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                aria-label={t("toasts.close")}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-secondary hover:text-content-primary"
                onClick={() => {
                  const timerId = timersRef.current[item.id];
                  if (timerId) {
                    window.clearTimeout(timerId);
                    delete timersRef.current[item.id];
                  }

                  setItems((currentItems) =>
                    currentItems.filter((toastItem) => toastItem.id !== item.id),
                  );
                }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
